import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { useCompanySettings } from '@/hooks/useSettings';
import { dispatchNotification } from '@/services/api';
import {
  NotificationChannel,
  NotificationEventKey,
  NotificationLocale,
  invitedAudienceFor,
} from '@dallali/notifications';
import {
  CheckCircle,
  Copy,
  ExternalLink,
  Mail,
  MessageSquare,
  Phone,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { extractInviteToken } from './inviteToken';

export interface InvitationSuccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inviteLink: string;
  whatsappMessage: string;
  recipientPhone: string;
  recipientName: string;
  recipientEmail?: string;
  profileType?: 'owner' | 'tenant' | 'employee' | 'service_provider';
}

export function InvitationSuccessModal({
  open,
  onOpenChange,
  inviteLink,
  whatsappMessage,
  recipientPhone,
  recipientName,
  recipientEmail,
  profileType,
}: InvitationSuccessModalProps) {
  const { t, language } = useLanguage();
  const { companyId } = useAuth();
  const { data: companySettings } = useCompanySettings();
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [isCopyingMessage, setIsCopyingMessage] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Channel picker — default WhatsApp (when a phone was entered) + Email (when
  // an email was entered). In-app is excluded: an invitee has no account yet.
  // A channel is only offered/selected if the recipient has the matching
  // contact field, so the inviter only ever sends on data they actually filled.
  const [selectedChannels, setSelectedChannels] = useState<
    Set<NotificationChannel>
  >(
    () =>
      new Set([
        ...(recipientPhone ? [NotificationChannel.Whatsapp] : []),
        ...(recipientEmail ? [NotificationChannel.Email] : []),
      ])
  );

  const toggleChannel = (channel: NotificationChannel, on: boolean) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (on) next.add(channel);
      else next.delete(channel);
      return next;
    });
  };

  const channelOptions: {
    channel: NotificationChannel;
    labelAr: string;
    labelEn: string;
    available: boolean;
  }[] = [
    {
      channel: NotificationChannel.Whatsapp,
      labelAr: 'واتساب',
      labelEn: 'WhatsApp',
      available: !!recipientPhone,
    },
    {
      channel: NotificationChannel.Email,
      labelAr: 'البريد الإلكتروني',
      labelEn: 'Email',
      available: !!recipientEmail,
    },
    {
      channel: NotificationChannel.Sms,
      labelAr: 'رسالة نصية',
      labelEn: 'SMS',
      available: !!recipientPhone,
    },
  ];

  const companyName =
    language === 'ar'
      ? (companySettings?.name ?? 'دلّالي')
      : (companySettings?.name_en ?? companySettings?.name ?? 'Dallali');
  const locale =
    language === 'ar' ? NotificationLocale.Ar : NotificationLocale.En;

  // Send the invitation through the unified notification dispatcher
  // (eventKey invite.user) on the selected channels in one call. The content
  // is rendered server-side from the ported invitation templates — same
  // wording as before, per portal type.
  const handleSend = async () => {
    if (!companyId) {
      toast.error(t('لا توجد شركة محددة', 'No company selected'));
      return;
    }
    const channels = [...selectedChannels];
    if (channels.length === 0) {
      toast.error(
        t('اختر قناة واحدة على الأقل', 'Select at least one channel')
      );
      return;
    }
    setIsSending(true);
    try {
      const { results } = await dispatchNotification({
        eventKey: NotificationEventKey.InviteUser,
        companyId,
        // Per-portal-type template selection (Option A).
        audience: invitedAudienceFor(profileType),
        recipient: {
          name: recipientName,
          phone: recipientPhone || undefined,
          email: recipientEmail || undefined,
          locale,
        },
        // `notification_link` is the canonical link token the seed templates
        // (email/SMS/in-app) render; pre-set it to the accept URL we already
        // hold (this path carries no invite_token, so getNotificationLink
        // can't build it). `invite_token` fills the WhatsApp accept-link
        // button (urlButtonVar in whatsapp-templates).
        context: {
          inviteLink,
          companyName,
          profileType,
          notification_link: inviteLink,
          invite_token: extractInviteToken(inviteLink),
        },
        channels,
      });
      const anySent = results.some((r) => r.status === 'sent');
      if (anySent) {
        setSent(true);
        toast.success(t('تم إرسال الدعوة', 'Invitation sent'));
      } else {
        toast.error(t('تعذّر إرسال الدعوة', 'Failed to send invitation'));
      }
    } catch {
      toast.error(t('تعذّر إرسال الدعوة', 'Failed to send invitation'));
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyLink = async () => {
    setIsCopyingLink(true);
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success(t('تم نسخ الرابط', 'Link copied'));
    } catch {
      toast.error(t('فشل النسخ', 'Failed to copy'));
    } finally {
      setTimeout(() => setIsCopyingLink(false), 1000);
    }
  };

  const handleCopyWhatsAppMessage = async () => {
    setIsCopyingMessage(true);
    try {
      await navigator.clipboard.writeText(whatsappMessage);
      toast.success(t('تم نسخ الرسالة', 'Message copied'));
    } catch {
      toast.error(t('فشل النسخ', 'Failed to copy'));
    } finally {
      setTimeout(() => setIsCopyingMessage(false), 1000);
    }
  };

  const handleOpenWhatsApp = () => {
    const cleanPhone = recipientPhone.replace(/\D/g, '');
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleOpenLink = () => {
    window.open(inviteLink, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <DialogTitle className="text-xl">
            {t('تم إنشاء رابط الدعوة!', 'Invitation Link Generated!')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'شارك الرابط مع المستخدم للانضمام إلى البوابة',
              'Share the link with the user to join the portal'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Copy WhatsApp Message Button */}
          <Button
            onClick={handleCopyWhatsAppMessage}
            className="w-full gap-2 h-12 text-base bg-[#25D366] hover:bg-[#22c55e]"
            disabled={isCopyingMessage}
          >
            <Copy className="h-5 w-5" />
            {t('نسخ رسالة واتساب', 'Copy WhatsApp Message')}
          </Button>

          {/* Open WhatsApp Button */}
          <Button
            onClick={handleOpenWhatsApp}
            variant="outline"
            className="w-full gap-2 h-11 text-base border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
          >
            <MessageSquare className="h-5 w-5" />
            {t('فتح واتساب', 'Open WhatsApp')}
          </Button>

          {/* Send directly via the platform — pick channels */}
          <div className="space-y-2 rounded-md border p-3">
            <Label className="text-sm">
              {t('إرسال مباشر عبر المنصة', 'Send directly via the platform')}
            </Label>
            <div className="flex flex-wrap gap-3">
              {channelOptions
                .filter((opt) => opt.available)
                .map((opt) => (
                  <label
                    key={opt.channel}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={selectedChannels.has(opt.channel)}
                      onCheckedChange={(c) =>
                        toggleChannel(opt.channel, c === true)
                      }
                    />
                    {t(opt.labelAr, opt.labelEn)}
                  </label>
                ))}
            </div>
            <Button
              onClick={handleSend}
              variant="outline"
              className="w-full gap-2 h-11 text-base"
              disabled={isSending || sent || selectedChannels.size === 0}
            >
              {sent ? (
                <Mail className="h-5 w-5" />
              ) : (
                <Phone className="h-5 w-5" />
              )}
              {sent
                ? t('تم إرسال الدعوة', 'Invitation Sent')
                : isSending
                  ? t('جاري الإرسال...', 'Sending...')
                  : t('إرسال الدعوة', 'Send Invitation')}
            </Button>
          </div>

          {/* Invitation Link */}
          <div className="space-y-2">
            <Label>{t('رابط الدعوة', 'Invitation Link')}</Label>
            <div className="flex gap-2">
              <Input
                data-testid="invite-link-input"
                readOnly
                value={inviteLink}
                className="text-sm font-mono bg-muted"
                dir="ltr"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyLink}
                disabled={isCopyingLink}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button variant="ghost" className="gap-1" onClick={handleOpenLink}>
            <ExternalLink className="h-4 w-4 rtl:rotate-180" />
            {t('فتح الرابط', 'Open Link')}
          </Button>
          <Button
            data-testid="invite-done-btn"
            onClick={() => onOpenChange(false)}
          >
            {t('تم', 'Done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
